import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';

import { useConfirmDialog } from '@bo-hooks/common/useModal';
import useToast from '@bo-hooks/common/useToast';
import poOpenApi from '@bo-apis/po/system/poOpenApi';
import GridButton from '@bo-components/common/grid/GridButton';
import Grid from '@bo-components/common/grid/Grid';
import openApiConst from './options/openApiConst';
import useOpenApiApprovalKey from './options/useOpenApiApprovalKey';
import ContentsTitle from '@bo-layout/contents/ContentsTitle';

const OpenApiManagementPage = () => {
  const { showConfirmDialog } = useConfirmDialog();
  const { showToast } = useToast();
  const [approvalKeyList, setApprovalKeyList] = useState([]);
  const [approvalKeyListTotal, setApprovalKeyListTotal] = useState(0);
  const approvalKeyGrid = useRef(null);

  const [certificationKeyList, setCertificationKeyList] = useState([]);
  const [certificationKeyListTotal, setCertificationKeyListTotal] = useState(0);
  const certificationKeyGrid = useRef(null);

  const { openApiApprovalOptions } = useOpenApiApprovalKey();

  const getApprovalKeyList = useCallback(
    async (pageable) => {
      const { result: { content: approvalKeyList = [], totalElements: total } = {} } =
        await poOpenApi.getOpenApiApprovalKeyList(pageable);
      setApprovalKeyList(approvalKeyList);
      setApprovalKeyListTotal(total);
    },
    [setApprovalKeyList, setApprovalKeyListTotal]
  );

  const handlePageableApprovalKeyListSearch = useCallback(
    async (pageIndex, pageSize, sort = {}) => await getApprovalKeyList({ size: pageSize, page: pageIndex, sort }),
    [getApprovalKeyList]
  );

  const getCertificationKeyList = useCallback(
    async (pageable) => {
      const { result: { content: certificationKeyList = [], totalElements: total } = {} } =
        await poOpenApi.getOpenApiCertificationKeyList(pageable);
      setCertificationKeyList(certificationKeyList);
      setCertificationKeyListTotal(total);
    },
    [setCertificationKeyList, setCertificationKeyListTotal]
  );

  const handlePageableCertificationKeyListSearch = useCallback(
    async (pageIndex, pageSize, sort = {}) => await getCertificationKeyList({ size: pageSize, page: pageIndex, sort }),
    [getCertificationKeyList]
  );

  const handleClickCreateApprovalKey = useCallback(() => {
    showConfirmDialog('새로운 승인KEY를 생성하시겠습니까?', async () => {
      const { result } = await poOpenApi.createOpenApiApprovalKey();

      if (result) {
        showToast('승인KEY가 생성되었습니다.');
      } else {
        showToast('승인KEY 생성에 실패하였습니다.');
      }

      approvalKeyGrid.current.setPageIndex(0);
      approvalKeyGrid.current.clearSort();

      await getApprovalKeyList({
        size: approvalKeyGrid.current.getPageSize(),
        page: 0,
        sort: approvalKeyGrid.current.getPageSort(),
      });
    });
  }, [showConfirmDialog, getApprovalKeyList, showToast]);

  const isDisabledInvalidateCertificationKey = useMemo(
    () => certificationKeyList.every((item) => item.useYn === 'N'),
    [certificationKeyList]
  );

  const handleClickInvalidateCertificationKey = useCallback(() => {
    showConfirmDialog('인증KEY를 무효화 하시겠습니까?', async () => {
      const { result } = await poOpenApi.invalidateOpenApiCertificationKey();

      if (result) {
        showToast('인증KEY가 무효화 되었습니다.');
      } else {
        showToast('인증KEY 무효화에 실패하였습니다.');
      }

      certificationKeyGrid.current.setPageIndex(0);
      certificationKeyGrid.current.clearSort();

      await getCertificationKeyList({
        size: certificationKeyGrid.current.getPageSize(),
        page: 0,
        sort: certificationKeyGrid.current.getPageSort(),
      });
    });
  }, [getCertificationKeyList, showConfirmDialog, showToast]);

  return (
    <>
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        <ContentsTitle>API 승인KEY 생성 내역</ContentsTitle>
        <Grid
          ref={approvalKeyGrid}
          options={openApiApprovalOptions}
          data={approvalKeyList}
          total={approvalKeyListTotal}
          onSearch={handlePageableApprovalKeyListSearch}
        >
          <GridButton
            type={'Primary'}
            label={'승인KEY생성'}
            width={'fit-content'}
            position={'right'}
            onClick={handleClickCreateApprovalKey}
          />
        </Grid>
        <Spacing bottom={36} width={'100%'} />
        <ContentsTitle>API 인증KEY 생성 내역</ContentsTitle>
        <Grid
          ref={certificationKeyGrid}
          options={openApiConst.openApiCertificationOptions}
          data={certificationKeyList}
          total={certificationKeyListTotal}
          onSearch={handlePageableCertificationKeyListSearch}
        >
          <GridButton
            type={'Primary'}
            disabled={isDisabledInvalidateCertificationKey}
            label={'인증KEY무효화'}
            width={'fit-content'}
            position={'right'}
            onClick={handleClickInvalidateCertificationKey}
          />
        </Grid>
      </FlexBoxColumn>
    </>
  );
};

export default OpenApiManagementPage;
